import { Component, type ErrorInfo, type ReactNode } from "react";
import { log } from "../../services/LogService";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    log.error("ErrorBoundary", error.message, { stack: errorInfo.componentStack });
  }

  public reset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error!, this.reset);
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8 bg-canvas dark:bg-[#131825]">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-red-100 dark:border-red-950 rounded-2xl p-6 shadow-xl space-y-4">
            <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400">
              <span className="text-2xl shrink-0">⚠️</span>
              <h3 className="text-base font-bold tracking-tight text-slate-800 dark:text-slate-100">
                Er is iets misgegaan / Something went wrong
              </h3>
            </div>

            <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {this.state.error?.message || "Er is een onbekende fout opgetreden bij het laden van dit scherm. Dit kan veroorzaakt worden door een synchronisatievertraging in OneDrive."}
            </p>

            <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-805">
              <button
                onClick={this.reset}
                className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white py-2 text-xs font-semibold transition-colors cursor-pointer focus:outline-none"
              >
                Opnieuw Proberen / Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
